import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { 
  Video,
  Music,
  Play, 
  Trash2, 
  CheckCircle,
  AlertCircle,
  Loader2,
  FolderOpen,
  FolderOutput,
  Settings,
  Scissors,
  ZoomIn,
  Crop,
  Download,
  Cpu
} from 'lucide-react';
import FeatureGuard from '../components/FeatureGuard';

function VideoAudioContent() {
  const [videoFiles, setVideoFiles] = useState([]);
  const [audioFiles, setAudioFiles] = useState([]);
  const [outputDirHandle, setOutputDirHandle] = useState(null);
  const [outputDirName, setOutputDirName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentFile: '', percent: 0 });
  const [results, setResults] = useState(null);
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [ffmpegLoading, setFfmpegLoading] = useState(false);
  
  const ffmpegRef = useRef(null);

  // Video editing settings
  const [videoSettings, setVideoSettings] = useState({
    startTrim: 0,
    endTrim: 0,
    scalePercent: 100,
    cropWidth: 0,
    cropHeight: 0,
    cropX: 0,
    cropY: 0,
    fadeOutDuration: 3
  });

  // Load FFmpeg on component mount
  const loadFFmpeg = useCallback(async () => {
    if (ffmpegLoaded || ffmpegLoading) return;
    
    setFfmpegLoading(true);
    try {
      const ffmpeg = new FFmpeg();
      ffmpegRef.current = ffmpeg;
      
      ffmpeg.on('progress', ({ progress, time }) => {
        setProgress(prev => ({ ...prev, percent: Math.round(progress * 100) }));
      });

      ffmpeg.on('log', ({ message }) => {
        console.log('[FFmpeg]', message);
      });

      // Load FFmpeg core with multi-threading support
      const baseURL = 'https://unpkg.com/@ffmpeg/core-mt@0.12.6/dist/esm';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        workerURL: await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript'),
      });

      setFfmpegLoaded(true);
      toast.success('FFmpeg yüklendi - Yerel işleme hazır');
    } catch (err) {
      console.error('FFmpeg load error:', err);
      toast.error('FFmpeg yüklenemedi: ' + err.message);
    } finally {
      setFfmpegLoading(false);
    }
  }, [ffmpegLoaded, ffmpegLoading]);

  useEffect(() => {
    loadFFmpeg();
  }, [loadFFmpeg]);

  const handleSelectVideoFolder = async () => {
    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
      const files = [];
      
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
          const file = await entry.getFile();
          const ext = file.name.toLowerCase().split('.').pop();
          if (['mp4', 'mov', 'mkv', 'avi', 'webm'].includes(ext)) {
            files.push({ name: file.name, file, handle: entry });
          }
        }
      }
      
      files.sort((a, b) => a.name.localeCompare(b.name));
      setVideoFiles(files);
      toast.success(`${files.length} video bulundu`);
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Klasör seçilemedi');
    }
  };

  const handleSelectAudioFolder = async () => {
    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
      const files = [];
      
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
          const file = await entry.getFile();
          const ext = file.name.toLowerCase().split('.').pop();
          if (['mp3', 'wav', 'aac', 'm4a'].includes(ext)) {
            files.push({ name: file.name, file, handle: entry });
          }
        }
      }
      
      files.sort((a, b) => a.name.localeCompare(b.name));
      setAudioFiles(files);
      toast.success(`${files.length} ses dosyası bulundu`);
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Klasör seçilemedi');
    }
  };

  const handleSelectOutputFolder = async () => {
    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
      setOutputDirHandle(dirHandle);
      setOutputDirName(dirHandle.name);
      toast.success(`Çıktı klasörü: ${dirHandle.name}`);
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Klasör seçilemedi');
    }
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Process video locally using FFmpeg.wasm
  const processVideoLocal = async (videoFile, audioFile) => {
    const ffmpeg = ffmpegRef.current;
    if (!ffmpeg) throw new Error('FFmpeg not loaded');

    const videoName = 'input_video.' + videoFile.name.split('.').pop();
    const audioName = 'input_audio.' + audioFile.name.split('.').pop();
    const outputName = 'output.mp4';

    // Write input files to FFmpeg virtual filesystem
    await ffmpeg.writeFile(videoName, await fetchFile(videoFile.file));
    await ffmpeg.writeFile(audioName, await fetchFile(audioFile.file));

    // Build FFmpeg command
    const args = ['-i', videoName, '-i', audioName];
    
    // Build filter complex
    const filters = [];
    
    // Video filters
    const videoFilters = [];
    
    // Scale if needed
    if (videoSettings.scalePercent !== 100) {
      const scale = videoSettings.scalePercent / 100;
      videoFilters.push(`scale=iw*${scale}:ih*${scale}`);
    }
    
    // Crop if dimensions specified
    if (videoSettings.cropWidth > 0 && videoSettings.cropHeight > 0) {
      videoFilters.push(`crop=${videoSettings.cropWidth}:${videoSettings.cropHeight}:${videoSettings.cropX}:${videoSettings.cropY}`);
    }
    
    // Audio filter - trim to video length and fade out
    const fadeOutDur = videoSettings.fadeOutDuration || 3;
    
    // Use complex filter for audio handling
    if (videoFilters.length > 0) {
      args.push('-vf', videoFilters.join(','));
    }
    
    // Map video from first input (without audio)
    args.push('-map', '0:v:0');
    // Map audio from second input
    args.push('-map', '1:a:0');
    
    // Audio settings - use shortest to match video length
    args.push('-shortest');
    
    // Add fade out to audio
    args.push('-af', `afade=t=out:st=0:d=${fadeOutDur}`);
    
    // Trim settings
    if (videoSettings.startTrim > 0) {
      args.push('-ss', String(videoSettings.startTrim));
    }
    if (videoSettings.endTrim > 0) {
      args.push('-t', String(videoSettings.endTrim));
    }
    
    // Output settings
    args.push('-c:v', 'libx264');
    args.push('-preset', 'fast');
    args.push('-crf', '23');
    args.push('-c:a', 'aac');
    args.push('-b:a', '192k');
    args.push('-y');
    args.push(outputName);

    console.log('FFmpeg command:', args.join(' '));
    
    // Execute FFmpeg
    await ffmpeg.exec(args);
    
    // Read output file
    const data = await ffmpeg.readFile(outputName);
    
    // Cleanup
    await ffmpeg.deleteFile(videoName);
    await ffmpeg.deleteFile(audioName);
    await ffmpeg.deleteFile(outputName);
    
    return new Blob([data.buffer], { type: 'video/mp4' });
  };

  const handleProcess = async () => {
    if (!ffmpegLoaded) {
      toast.error('FFmpeg henüz yüklenmedi, lütfen bekleyin');
      return;
    }

    if (videoFiles.length === 0 || audioFiles.length === 0) {
      toast.error('Lütfen video ve ses klasörlerini seçin');
      return;
    }

    if (!outputDirHandle) {
      toast.error('Lütfen çıktı klasörünü seçin');
      return;
    }

    const shuffledVideos = shuffleArray(videoFiles);
    const pairCount = Math.min(shuffledVideos.length, audioFiles.length);
    
    setProcessing(true);
    setResults(null);
    setProgress({ current: 0, total: pairCount, currentFile: '', percent: 0 });

    const processedFiles = [];
    const errors = [];

    for (let i = 0; i < pairCount; i++) {
      const video = shuffledVideos[i];
      const audio = audioFiles[i];
      
      setProgress({ current: i + 1, total: pairCount, currentFile: video.name, percent: 0 });

      try {
        // Process locally using FFmpeg.wasm
        const blob = await processVideoLocal(video, audio);
        
        // Save to output folder
        const outputFileName = video.name.replace(/\.[^/.]+$/, '_merged.mp4');
        const fileHandle = await outputDirHandle.getFileHandle(outputFileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();

        processedFiles.push(outputFileName);
        console.log(`✅ Saved: ${outputFileName}`);
      } catch (err) {
        console.error(`Error processing ${video.name}:`, err);
        errors.push({ file: video.name, error: err.message || 'İşleme hatası' });
      }
    }

    setResults({ processed: processedFiles, errors });
    setProcessing(false);
    
    if (errors.length === 0) {
      toast.success(`${processedFiles.length} video başarıyla işlendi`);
    } else if (processedFiles.length > 0) {
      toast.warning(`${processedFiles.length} başarılı, ${errors.length} hata`);
    } else {
      toast.error('Tüm dosyalarda hata oluştu');
    }
  };

  const handleClear = () => {
    setVideoFiles([]);
    setAudioFiles([]);
    setOutputDirHandle(null);
    setOutputDirName('');
    setResults(null);
    setProgress({ current: 0, total: 0, currentFile: '', percent: 0 });
    toast.info('Temizlendi');
  };

  const pairCount = Math.min(videoFiles.length, audioFiles.length);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="video-audio-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Video + Ses Birleştir
        </h1>
        <p className="text-gray-600 mt-1 flex items-center gap-2">
          <Cpu className="w-4 h-4" />
          {ffmpegLoaded ? (
            <span className="text-green-600">✓ Yerel İşleme Aktif (Bilgisayarınızda)</span>
          ) : ffmpegLoading ? (
            <span className="text-yellow-600">FFmpeg yükleniyor...</span>
          ) : (
            <span className="text-gray-500">FFmpeg hazırlanıyor...</span>
          )}
        </p>
      </div>

      {/* Folder Selection */}
      <div className="grid gap-4 lg:grid-cols-3 mb-6">
        <Card className="shadow border-0 bg-white/80">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Video className="w-5 h-5 text-purple-500" /> Video Klasörü
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSelectVideoFolder} disabled={processing} variant="outline" className="w-full h-16 border-2 border-dashed">
              <FolderOpen className="w-5 h-5 mr-2" /> Seç
            </Button>
            {videoFiles.length > 0 && (
              <p className="mt-2 text-sm text-purple-600 font-medium">{videoFiles.length} video</p>
            )}
          </CardContent>
        </Card>

        <Card className="shadow border-0 bg-white/80">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Music className="w-5 h-5 text-pink-500" /> Ses Klasörü
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSelectAudioFolder} disabled={processing} variant="outline" className="w-full h-16 border-2 border-dashed">
              <FolderOpen className="w-5 h-5 mr-2" /> Seç
            </Button>
            {audioFiles.length > 0 && (
              <p className="mt-2 text-sm text-pink-600 font-medium">{audioFiles.length} ses</p>
            )}
          </CardContent>
        </Card>

        <Card className="shadow border-0 bg-white/80">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <FolderOutput className="w-5 h-5 text-green-500" /> Çıktı Klasörü
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSelectOutputFolder} disabled={processing} variant="outline" className="w-full h-16 border-2 border-dashed">
              <FolderOpen className="w-5 h-5 mr-2" /> Seç
            </Button>
            {outputDirName && (
              <p className="mt-2 text-sm text-green-600 font-medium truncate">{outputDirName}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Video Settings */}
      <Card className="shadow border-0 bg-white/80 mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Settings className="w-5 h-5 text-blue-500" /> Video Ayarları
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Trim Settings */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm">
                <Scissors className="w-4 h-4" /> Baştan Kes (sn)
              </Label>
              <Input
                type="number"
                min="0"
                value={videoSettings.startTrim}
                onChange={(e) => setVideoSettings(s => ({ ...s, startTrim: parseFloat(e.target.value) || 0 }))}
                disabled={processing}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm">
                <Scissors className="w-4 h-4" /> Süre Limiti (sn)
              </Label>
              <Input
                type="number"
                min="0"
                value={videoSettings.endTrim}
                onChange={(e) => setVideoSettings(s => ({ ...s, endTrim: parseFloat(e.target.value) || 0 }))}
                disabled={processing}
                placeholder="0 = limit yok"
              />
            </div>

            {/* Scale Setting */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm">
                <ZoomIn className="w-4 h-4" /> Ölçek (%)
              </Label>
              <Input
                type="number"
                min="10"
                max="200"
                value={videoSettings.scalePercent}
                onChange={(e) => setVideoSettings(s => ({ ...s, scalePercent: parseInt(e.target.value) || 100 }))}
                disabled={processing}
              />
            </div>

            {/* Fade Out Duration */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm">
                <Music className="w-4 h-4" /> Ses Fade Out (sn)
              </Label>
              <Input
                type="number"
                min="0"
                max="30"
                value={videoSettings.fadeOutDuration}
                onChange={(e) => setVideoSettings(s => ({ ...s, fadeOutDuration: parseFloat(e.target.value) || 3 }))}
                disabled={processing}
              />
            </div>
          </div>

          {/* Crop Settings */}
          <div className="mt-4 pt-4 border-t">
            <Label className="flex items-center gap-1 text-sm mb-3">
              <Crop className="w-4 h-4" /> Kırpma (0 = kapalı)
            </Label>
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Genişlik</Label>
                <Input
                  type="number"
                  min="0"
                  value={videoSettings.cropWidth}
                  onChange={(e) => setVideoSettings(s => ({ ...s, cropWidth: parseInt(e.target.value) || 0 }))}
                  disabled={processing}
                  placeholder="px"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Yükseklik</Label>
                <Input
                  type="number"
                  min="0"
                  value={videoSettings.cropHeight}
                  onChange={(e) => setVideoSettings(s => ({ ...s, cropHeight: parseInt(e.target.value) || 0 }))}
                  disabled={processing}
                  placeholder="px"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">X Pozisyon</Label>
                <Input
                  type="number"
                  min="0"
                  value={videoSettings.cropX}
                  onChange={(e) => setVideoSettings(s => ({ ...s, cropX: parseInt(e.target.value) || 0 }))}
                  disabled={processing}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Y Pozisyon</Label>
                <Input
                  type="number"
                  min="0"
                  value={videoSettings.cropY}
                  onChange={(e) => setVideoSettings(s => ({ ...s, cropY: parseInt(e.target.value) || 0 }))}
                  disabled={processing}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          onClick={handleProcess}
          disabled={processing || pairCount === 0 || !outputDirHandle || !ffmpegLoaded}
          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white min-w-[140px]"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              İşleniyor...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Başlat ({pairCount})
            </>
          )}
        </Button>

        <Button
          onClick={handleClear}
          disabled={processing}
          variant="outline"
          className="border-red-200 text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Temizle
        </Button>
      </div>

      {/* Progress */}
      {processing && (
        <Card className="shadow border-0 bg-gradient-to-r from-purple-50 to-pink-50 mb-6">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
              <div className="flex-1">
                <p className="font-medium text-purple-900">
                  {progress.current}/{progress.total}: {progress.currentFile}
                </p>
                <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
                {progress.percent > 0 && (
                  <p className="text-xs text-purple-600 mt-1">Dosya ilerleme: %{progress.percent}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && (
        <Card className="shadow border-0 bg-white/80">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              {results.errors.length === 0 ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-yellow-500" />
              )}
              Sonuçlar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {results.processed.length > 0 && (
              <div className="mb-4">
                <p className="text-green-600 font-medium mb-2">✓ {results.processed.length} başarılı</p>
                <div className="max-h-40 overflow-y-auto text-sm text-gray-600 space-y-1">
                  {results.processed.map((file, i) => (
                    <p key={i} className="flex items-center gap-2">
                      <Download className="w-3 h-3" /> {file}
                    </p>
                  ))}
                </div>
              </div>
            )}
            
            {results.errors.length > 0 && (
              <div>
                <p className="text-red-600 font-medium mb-2">✗ {results.errors.length} hata</p>
                <div className="max-h-40 overflow-y-auto text-sm space-y-1">
                  {results.errors.map((err, i) => (
                    <p key={i} className="text-red-500">
                      {err.file}: {err.error}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// Wrapper with FeatureGuard
export default function VideoAudio() {
  return (
    <FeatureGuard featureKey="video_audio" pageName="Video + Ses">
      <VideoAudioContent />
    </FeatureGuard>
  );
}
